import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../utils/rolePermissions';

interface PermissionRouteProps {
    children: React.ReactNode;
    requiredPermissions?: Permission[];
    requireAny?: boolean; // If true, user needs ANY of the permissions. If false, user needs ALL permissions.
    fallbackPath?: string;
}

/**
 * A route component that checks if the user has the required permissions
 * If the user doesn't have the required permissions, they are redirected to a fallback path
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
    children,
    requiredPermissions = [],
    requireAny = false,
    fallbackPath = '/dashboard'
}) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const { hasPermission, hasAnyPermission, isAdmin } = usePermissions();
    const location = useLocation();

    // Wait for the profile as well as the sign-in check. Permissions are read from the user's
    // groups, which arrive with the profile, so checking any earlier tests an account that has no
    // roles yet - every check fails and the user is bounced to the fallback. That is what made a
    // refresh, a bookmark or a shared link land on the dashboard instead of the page asked for.
    if (isLoading || (isAuthenticated && !user)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/auth-signin" state={{ from: location }} replace />;
    }

    // If no permissions required, allow access
    if (requiredPermissions.length === 0) {
        return <>{children}</>;
    }

    // Admin users bypass all permission checks
    if (isAdmin()) {
        return <>{children}</>;
    }

    // Check permissions
    let hasAccess = false;
    if (requireAny) {
        // User needs ANY of the specified permissions
        hasAccess = hasAnyPermission(requiredPermissions);
    } else {
        // User needs ALL of the specified permissions
        hasAccess = requiredPermissions.every(permission => hasPermission(permission));
    }

    // If user doesn't have access, redirect to fallback path
    if (!hasAccess) {
        return <Navigate to={fallbackPath} replace />;
    }

    return <>{children}</>;
};
